import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getAllFailedPurchases,
  searchFailedPurchases,
  updateFailedPurchaseStatus,
  fetchAllFailedPurchasesForExport,
  FailedPurchaseListOptions,
} from "@/services/firebase/failedPurchase";
import { FailedPurchase } from "@/types/failedPurchase";
import { useAuth } from "@/hooks/useAuth";
import { useDebounce } from "@/hooks/useDebounce";
import CopyableId from "@/components/common/CopyableId";
import { QueryDocumentSnapshot, DocumentData } from "firebase/firestore";
import { exportToCSV } from "@/utils/csvExport";
import { X, Filter, Download, Loader2 } from "lucide-react";
import { AdminPage } from "@/components/admin-ui/admin-page";
import { AdminPageHeader } from "@/components/admin-ui/admin-page-header";
import { AdminSearchInput } from "@/components/admin-ui/admin-search-input";
import {
  AdminDataTable,
  AdminTableColumn,
} from "@/components/admin-ui/admin-data-table";
import { AdminLoadingSkeleton } from "@/components/admin-ui/admin-loading-skeleton";
import { AdminLoadingOverlay } from "@/components/admin-ui/admin-loading-overlay";
import { AdminErrorAlert } from "@/components/admin-ui/admin-error-alert";
import { AdminEmptyState } from "@/components/admin-ui/admin-empty-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

// 狀態標籤配置
const statusConfig: Record<string, { label: string; variant: string }> = {
  pending: { label: "待處理", variant: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200" },
  investigating: { label: "調查中", variant: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" },
  resolved: { label: "已解決", variant: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" },
  rejected: { label: "已拒絕", variant: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200" },
};

// 類型標籤配置
const typeLabels: Record<string, string> = {
  coin_purchase: "購買金幣",
};

// 平台標籤配置
const platformLabels: Record<string, string> = {
  ios: "iOS",
  android: "Android",
};

// 失敗階段標籤配置
const failureStageLabels: Record<string, string> = {
  validation: "驗證階段",
  verification: "確認階段",
  database: "資料庫階段",
  unknown: "未知",
};

interface FailedPurchaseFilters {
  status?: FailedPurchase["status"];
  type?: FailedPurchase["type"];
  platform?: FailedPurchase["platform"];
  failure_stage?: FailedPurchase["failure_stage"];
}

const EXPORT_MAX_RECORDS = 10000;

const columns: AdminTableColumn[] = [
  { key: "transaction_id", label: "交易 ID" },
  { key: "user_id", label: "用戶 ID" },
  { key: "type", label: "類型", hideBelow: "sm" },
  { key: "platform", label: "平台", hideBelow: "sm" },
  { key: "error", label: "錯誤碼", hideBelow: "md" },
  { key: "failure_stage", label: "失敗階段", hideBelow: "md" },
  { key: "status", label: "狀態" },
  { key: "timestamp", label: "時間", sortable: true, hideBelow: "sm" },
  { key: "actions", label: "操作" },
];

export default function FailedPurchaseListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<FailedPurchaseFilters>({});
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState<FailedPurchase | null>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState<FailedPurchase["status"]>("pending");
  const [resolutionNotes, setResolutionNotes] = useState("");

  // 分頁狀態
  const [allFailedPurchases, setAllFailedPurchases] = useState<FailedPurchase[]>([]);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // 匯出狀態
  const [isExporting, setIsExporting] = useState(false);

  // 搜尋查詢
  const {
    data: searchResults,
    isLoading: isSearching,
    error: searchError,
    refetch: refetchSearch,
  } = useQuery({
    queryKey: ["failed-purchases", "search", debouncedSearchQuery],
    queryFn: () => searchFailedPurchases(debouncedSearchQuery),
    enabled: false,
  });

  // 列表查詢
  const {
    data: failedPurchaseResult,
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: ["failed-purchases", "list", filters.status, filters.type, filters.platform, filters.failure_stage],
    queryFn: async () => {
      const options: FailedPurchaseListOptions = {
        filters: {
          status: filters.status,
          type: filters.type,
          platform: filters.platform,
          failure_stage: filters.failure_stage,
        },
        limit: 50,
      };
      return getAllFailedPurchases(options);
    },
    enabled: !hasSearched || !debouncedSearchQuery.trim(),
  });

  // 當 filters 改變 / 首次載入時同步分頁 state（React Query v5 已移除 onSuccess）
  useEffect(() => {
    if (!hasSearched && failedPurchaseResult) {
      setAllFailedPurchases(failedPurchaseResult.failedPurchases);
      setLastDoc(failedPurchaseResult.lastDoc);
      setHasMore(failedPurchaseResult.hasMore);
    }
  }, [failedPurchaseResult, hasSearched]);

  // 載入更多
  const loadMore = useCallback(async () => {
    if (!lastDoc || isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    try {
      const options: FailedPurchaseListOptions = {
        filters: {
          status: filters.status,
          type: filters.type,
          platform: filters.platform,
          failure_stage: filters.failure_stage,
        },
        limit: 50,
        startAfter: lastDoc,
      };
      const result = await getAllFailedPurchases(options);
      setAllFailedPurchases((prev) => [...prev, ...result.failedPurchases]);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (err) {
      console.error("Error loading more failed purchases:", err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [lastDoc, isLoadingMore, hasMore, filters]);

  // 更新狀態的 mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({
      transactionId,
      status,
      resolutionNotes,
    }: {
      transactionId: string;
      status: FailedPurchase["status"];
      resolutionNotes?: string;
    }) =>
      updateFailedPurchaseStatus(
        transactionId,
        status,
        resolutionNotes,
        user?.uid
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["failed-purchases"] });
      setShowStatusModal(false);
      setSelectedPurchase(null);
      setResolutionNotes("");
    },
  });

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      setHasSearched(false);
      return;
    }
    setHasSearched(true);
    await refetchSearch();
  };

  const displayFailedPurchases = useMemo(() => {
    if (hasSearched && debouncedSearchQuery.trim() && searchResults) {
      return searchResults;
    }
    return allFailedPurchases;
  }, [hasSearched, debouncedSearchQuery, searchResults, allFailedPurchases]);

  const handleUpdateStatus = (purchase: FailedPurchase) => {
    setSelectedPurchase(purchase);
    setNewStatus(purchase.status || "pending");
    setResolutionNotes(purchase.resolution_notes || "");
    setShowStatusModal(true);
  };

  const handleConfirmStatusUpdate = () => {
    if (!selectedPurchase) return;

    updateStatusMutation.mutate({
      transactionId: selectedPurchase.id,
      status: newStatus,
      resolutionNotes: resolutionNotes.trim() || undefined,
    });
  };

  const formatDateTime = (date: Date | string | undefined): string => {
    if (!date) return "-";
    const d = typeof date === "string" ? new Date(date) : date;
    return d.toLocaleString("zh-TW");
  };

  const handleClearFilters = () => {
    setFilters({});
    setSearchQuery("");
    setHasSearched(false);
    setAllFailedPurchases([]);
    setLastDoc(null);
    setHasMore(false);
  };

  const toCsvRow = (fp: FailedPurchase) => ({
    交易ID: fp.transaction_id,
    用戶ID: fp.user_id,
    類型: typeLabels[fp.type] || fp.type,
    平台: platformLabels[fp.platform] || fp.platform,
    錯誤碼: fp.error_code || "",
    錯誤訊息: fp.error_message || "",
    失敗階段: failureStageLabels[fp.failure_stage] || fp.failure_stage,
    狀態: statusConfig[fp.status || "pending"]?.label || fp.status || "",
    時間: fp.timestamp ?? new Date(),
    處理備註: fp.resolution_notes || "",
  });

  const csvHeaders = [
    { key: "交易ID" as const, label: "交易 ID" },
    { key: "用戶ID" as const, label: "用戶 ID" },
    { key: "類型" as const, label: "類型" },
    { key: "平台" as const, label: "平台" },
    { key: "錯誤碼" as const, label: "錯誤碼" },
    { key: "錯誤訊息" as const, label: "錯誤訊息" },
    { key: "失敗階段" as const, label: "失敗階段" },
    { key: "狀態" as const, label: "狀態" },
    { key: "時間" as const, label: "時間" },
    { key: "處理備註" as const, label: "處理備註" },
  ];

  const buildFilename = () => {
    const now = new Date();
    const ymd = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}`;
    const hms = `${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}${String(now.getSeconds()).padStart(2, "0")}`;
    return `失敗購買記錄_${ymd}_${hms}`;
  };

  const handleExportCSV = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      if (hasSearched && debouncedSearchQuery.trim() && searchResults) {
        if (searchResults.length === 0) {
          alert("沒有資料可以匯出");
          return;
        }
        exportToCSV(searchResults.map(toCsvRow), csvHeaders, buildFilename());
        return;
      }

      const { failedPurchases, truncated, total } =
        await fetchAllFailedPurchasesForExport(filters, {
          maxRecords: EXPORT_MAX_RECORDS,
        });

      if (total === 0) {
        alert("沒有資料可以匯出");
        return;
      }

      if (truncated) {
        const proceed = window.confirm(
          `篩選結果超過 ${EXPORT_MAX_RECORDS.toLocaleString()} 筆，僅會匯出最新的 ${total.toLocaleString()} 筆。\n建議使用更嚴格的篩選條件。\n\n仍要繼續匯出？`
        );
        if (!proceed) return;
      }

      exportToCSV(failedPurchases.map(toCsvRow), csvHeaders, buildFilename());
    } catch (err) {
      console.error("匯出 CSV 失敗:", err);
      alert("匯出 CSV 時發生錯誤，請稍後再試");
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, hasSearched, debouncedSearchQuery, searchResults, filters]);

  const renderCell = (failed: FailedPurchase, column: AdminTableColumn) => {
    switch (column.key) {
      case "transaction_id":
        return <CopyableId id={failed.transaction_id} maxLength={16} />;
      case "user_id":
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/users/${failed.user_id}`);
            }}
            className="text-primary hover:text-primary/80 hover:underline"
          >
            <CopyableId id={failed.user_id} maxLength={12} />
          </button>
        );
      case "type":
        return (
          <span className="text-sm text-muted-foreground">
            {typeLabels[failed.type] || failed.type}
          </span>
        );
      case "platform":
        return (
          <span className="text-sm text-muted-foreground">
            {platformLabels[failed.platform] || failed.platform}
          </span>
        );
      case "error":
        return (
          <div className="text-sm text-muted-foreground">
            <div className="font-mono">{failed.error_code}</div>
            <div className="text-xs text-muted-foreground/70 mt-1 max-w-xs truncate">
              {failed.error_message}
            </div>
          </div>
        );
      case "failure_stage":
        return (
          <span className="text-sm text-muted-foreground">
            {failureStageLabels[failed.failure_stage] || failed.failure_stage}
          </span>
        );
      case "status": {
        const cfg = statusConfig[failed.status || "pending"];
        return (
          <Badge
            variant="secondary"
            className={`border-transparent text-xs font-semibold ${cfg?.variant || ""}`}
          >
            {cfg?.label || failed.status}
          </Badge>
        );
      }
      case "timestamp":
        return (
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {formatDateTime(failed.timestamp)}
          </span>
        );
      case "actions":
        return (
          <Button
            variant="link"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleUpdateStatus(failed);
            }}
          >
            更新狀態
          </Button>
        );
      default:
        return null;
    }
  };

  const hasAnyFilter =
    filters.status != null ||
    filters.type != null ||
    filters.platform != null ||
    filters.failure_stage != null;

  // 僅首次冷載入顯示 skeleton
  const showInitialSkeleton =
    (isLoading && allFailedPurchases.length === 0) ||
    (isSearching && hasSearched && !searchResults);

  if (showInitialSkeleton) {
    return (
      <AdminPage>
        <AdminPageHeader title="失敗購買記錄" subtitle="追蹤與處理購買失敗記錄" />
        <AdminLoadingSkeleton />
      </AdminPage>
    );
  }

  if (error || searchError) {
    return (
      <AdminPage>
        <AdminPageHeader title="失敗購買記錄" subtitle="追蹤與處理購買失敗記錄" />
        <AdminErrorAlert
          message={`載入失敗購買記錄時發生錯誤：${
            (error || searchError) instanceof Error
              ? (error || searchError)?.message
              : "未知錯誤"
          }`}
        />
      </AdminPage>
    );
  }

  const overlayVisible =
    (isFetching && !isLoading) || (isSearching && hasSearched);

  return (
    <AdminPage>
      <AdminPageHeader
        title="失敗購買記錄"
        subtitle="追蹤與處理購買失敗記錄"
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            disabled={isExporting}
            title={hasAnyFilter ? "匯出所有篩選後的資料" : "匯出所有資料"}
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-1" />
            )}
            {isExporting ? "匯出中…" : "匯出 CSV"}
          </Button>
        }
      />

      {/* 搜尋和篩選 */}
      <div className="space-y-4 mb-6">
        <form onSubmit={handleSearch} className="flex items-center gap-3">
          <AdminSearchInput
            value={searchQuery}
            onChange={(v) => {
              setSearchQuery(v);
              setHasSearched(false);
            }}
            placeholder="搜尋交易 ID、用戶 ID、錯誤碼或錯誤訊息"
            className="max-w-md"
          />
          <Button type="submit" size="sm" disabled={isSearching || !searchQuery.trim()}>
            {isSearching ? "搜尋中..." : "搜尋"}
          </Button>
          {hasSearched && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchQuery("");
                setHasSearched(false);
              }}
            >
              <X className="h-4 w-4 mr-1" />
              清除
            </Button>
          )}
        </form>

        {/* 篩選器 */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">狀態</label>
            <Select
              value={filters.status || "all"}
              onValueChange={(v) =>
                setFilters({
                  ...filters,
                  status: v === "all" ? undefined : (v as FailedPurchase["status"]),
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="全部" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部</SelectItem>
                <SelectItem value="pending">待處理</SelectItem>
                <SelectItem value="investigating">調查中</SelectItem>
                <SelectItem value="resolved">已解決</SelectItem>
                <SelectItem value="rejected">已拒絕</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">類型</label>
            <Select
              value={filters.type || "all"}
              onValueChange={(v) =>
                setFilters({
                  ...filters,
                  type: v === "all" ? undefined : (v as FailedPurchase["type"]),
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="全部" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部</SelectItem>
                <SelectItem value="coin_purchase">購買金幣</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">平台</label>
            <Select
              value={filters.platform || "all"}
              onValueChange={(v) =>
                setFilters({
                  ...filters,
                  platform: v === "all" ? undefined : (v as FailedPurchase["platform"]),
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="全部" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部</SelectItem>
                <SelectItem value="ios">iOS</SelectItem>
                <SelectItem value="android">Android</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">失敗階段</label>
            <Select
              value={filters.failure_stage || "all"}
              onValueChange={(v) =>
                setFilters({
                  ...filters,
                  failure_stage: v === "all" ? undefined : (v as FailedPurchase["failure_stage"]),
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="全部" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部</SelectItem>
                <SelectItem value="validation">驗證階段</SelectItem>
                <SelectItem value="verification">確認階段</SelectItem>
                <SelectItem value="database">資料庫階段</SelectItem>
                <SelectItem value="unknown">未知</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-end">
            <Button variant="outline" size="sm" onClick={handleClearFilters} className="w-full">
              <Filter className="h-4 w-4 mr-1" />
              清除篩選
            </Button>
          </div>
        </div>
      </div>

      {/* 失敗購買記錄列表 */}
      {displayFailedPurchases && displayFailedPurchases.length > 0 ? (
        <AdminLoadingOverlay visible={overlayVisible} label="資料更新中…">
          <div className="space-y-0">
            <AdminDataTable
              columns={columns}
              data={displayFailedPurchases}
              renderCell={renderCell}
              getRowId={(fp) => fp.id}
            />

            {!hasSearched && hasMore && (
              <div className="flex justify-center py-4">
                <Button onClick={loadMore} disabled={isLoadingMore}>
                  {isLoadingMore ? "載入中..." : "載入更多"}
                </Button>
              </div>
            )}

            {!hasSearched && !hasMore && allFailedPurchases.length > 0 && (
              <div className="px-4 py-3 border-t text-xs text-muted-foreground">
                已載入全部 {allFailedPurchases.length} 筆
              </div>
            )}

            {hasSearched && (
              <div className="px-4 py-3 border-t text-xs text-muted-foreground">
                搜尋結果：共 {displayFailedPurchases.length} 筆
              </div>
            )}
          </div>
        </AdminLoadingOverlay>
      ) : (
        <AdminLoadingOverlay visible={overlayVisible} label="資料更新中…">
          <AdminEmptyState
            title={hasSearched ? "未找到符合條件的失敗購買記錄" : "尚無失敗購買記錄"}
          />
        </AdminLoadingOverlay>
      )}

      {/* 狀態更新 Modal */}
      {showStatusModal && selectedPurchase && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-xl shadow-lg p-6 max-w-md w-full mx-4 border">
            <h2 className="text-xl font-bold text-foreground mb-4">
              更新失敗購買記錄狀態
            </h2>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  交易 ID
                </label>
                <div className="px-4 py-2 bg-muted rounded-lg text-sm font-mono text-muted-foreground">
                  {selectedPurchase.transaction_id}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  狀態
                </label>
                <Select
                  value={newStatus}
                  onValueChange={(v) => setNewStatus(v as FailedPurchase["status"])}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">待處理</SelectItem>
                    <SelectItem value="investigating">調查中</SelectItem>
                    <SelectItem value="resolved">已解決</SelectItem>
                    <SelectItem value="rejected">已拒絕</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  處理備註（選填）
                </label>
                <textarea
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  rows={4}
                  placeholder="輸入處理備註..."
                  className="w-full px-4 py-2 border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowStatusModal(false);
                  setSelectedPurchase(null);
                  setResolutionNotes("");
                }}
              >
                取消
              </Button>
              <Button
                className="flex-1"
                onClick={handleConfirmStatusUpdate}
                disabled={updateStatusMutation.isPending}
              >
                {updateStatusMutation.isPending ? "更新中..." : "確認更新"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminPage>
  );
}
